import { Column } from "@/components/Column";
import { CopyButton } from "@/components/CopyButton";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { SadieSimulatorPromptDiffBlock } from "@/components/tools/sadie-simulator/SadieSimulatorPromptDiffBlock";
import { SadieSimulatorTools } from "@/components/tools/sadie-simulator/SadieSimulatorTools";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@ui/tabs";

import { sadieSimulator } from "@/lib/sadieSimulator";
import { SadieSimulatorPrompt, SadieSimulatorPromptChange } from "@/types";

// The whole prompt Sadie's last turn ran on, as built and with the PM's
// changes on it, marked where the two differ, and the tools she had with it.
export const SadieSimulatorPromptViewer = ({
  changes,
  onClose,
  prompt,
}: {
  changes: SadieSimulatorPromptChange[];
  onClose: () => void;
  prompt: SadieSimulatorPrompt;
}) => {
  /* Helpers */
  const production = sadieSimulator.promptText(prompt.sections);
  const diff = sadieSimulator.promptDiff(prompt.sections, changes);
  const edited = sadieSimulator.promptText(
    diff.filter((section) => section.status !== "REMOVED"),
  );
  const builtAt = new Date(prompt.builtAt).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });

  /* Render */
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="flex h-[85vh] max-w-5xl flex-col gap-3">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-slate-700">
            Sadie&apos;s prompt
          </DialogTitle>
          <Text className="text-xs font-normal text-slate-500">
            {`From her last turn, at ${builtAt}. Her facts, like THIS TURN and ON FILE, update every turn.`}
          </Text>
        </DialogHeader>
        <Tabs
          className="flex min-h-0 flex-1 flex-col gap-2"
          defaultValue={changes.length > 0 ? "EDITED" : "PRODUCTION"}
        >
          <TabsList className="self-start">
            <TabsTrigger value="PRODUCTION">Production</TabsTrigger>
            <TabsTrigger disabled={changes.length === 0} value="EDITED">
              With your changes
            </TabsTrigger>
            <TabsTrigger value="TOOLS">Her tools</TabsTrigger>
          </TabsList>
          <TabsContent className="mt-0 min-h-0 flex-1" value="PRODUCTION">
            <PromptPane toCopy={production}>
              <Text className="whitespace-pre-wrap font-mono text-xs font-normal text-slate-800">
                {production}
              </Text>
            </PromptPane>
          </TabsContent>
          <TabsContent className="mt-0 min-h-0 flex-1" value="EDITED">
            <PromptPane toCopy={edited}>
              {diff.map((section) => (
                <SadieSimulatorPromptDiffBlock
                  key={`${section.status}-${section.heading}`}
                  section={section}
                />
              ))}
            </PromptPane>
          </TabsContent>
          <TabsContent className="mt-0 min-h-0 flex-1" value="TOOLS">
            <Column className="h-full min-h-0 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-4">
              <SadieSimulatorTools prompt={prompt} />
            </Column>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

// Scrolls on its own, with a copy of the whole prompt above it.
const PromptPane = ({
  children,
  toCopy,
}: {
  children: React.ReactNode;
  toCopy: string;
}) => (
  <Column className="h-full min-h-0 gap-2">
    <Row className="items-center justify-end gap-2">
      <Text className="text-xs font-normal text-slate-500">Copy it all</Text>
      <CopyButton toCopy={toCopy} />
    </Row>
    <Column className="min-h-0 flex-1 gap-4 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-4">
      {children}
    </Column>
  </Column>
);
